import { routerPath } from "utils/publicUrl";

/**
 * Shared utilities for Sentry URL and request classification
 * Used by both Axios interceptors and error filtering
 */

/**
 * Extracts the service name from the first part of the API URL path
 * Only returns service name for same-site requests
 * @param url - The API URL to analyze
 * @returns Service name or undefined if not a same-site API call
 */
export function getServiceName(url?: string): string | undefined {
  if (!url) return undefined;

  try {
    const urlObj = new URL(url, window.location.origin);

    // Only extract service name for same-site requests
    if (urlObj.hostname !== window.location.hostname) {
      return undefined;
    }

    // Extract the first path segment as service name
    // e.g., "/data/api/v1/well" -> "data". The app's own base is not a
    // service: under /eva every same-site request would otherwise be "eva".
    const pathSegments = routerPath(urlObj.pathname)
      .split("/")
      .filter((segment) => segment.length > 0);

    if (pathSegments.length > 0) {
      return pathSegments[0];
    }

    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Sanitizes URL for logging by removing sensitive parameters
 */
export function getCleanUrl(url?: string): string {
  if (!url) return "unknown";

  try {
    const urlObj = new URL(url, window.location.origin);

    // Remove sensitive query parameters
    const sensitiveParams = ["token", "key", "password", "auth", "secret"];
    sensitiveParams.forEach((param) => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, "[REDACTED]");
      }
    });

    return urlObj.pathname + (urlObj.search ? urlObj.search : "");
  } catch {
    // If URL parsing fails, return sanitized string
    return url.split("?")[0] || url;
  }
}

/**
 * Keeps only the origin and path; redirect URLs can carry tokens or auth codes
 * in the query or fragment
 */
export function getUrlOriginAndPath(url?: string): Maybe<string> {
  if (!url) return undefined;

  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin + urlObj.pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
}
